import { useEffect, useState } from 'react'
import { SwtchError } from '../models/error'
import { PaginationMeta } from '../models/pagination'
import { GetTaxConfigurations, TaxConfigurationResponse, TaxConfigurationFilters } from '../services/data-provider/tax'

export interface TaxRule {
  id: string
  name: string
  description: string
  locationType: 'states' | 'zip'
  locationDetails: string[]
  rateType: 'perKwh' | 'percentage'
  rateCents?: number
  ratePercentage?: number
  appliesTo?: 'allCost' | 'energyCostOnly'
  powerThresholdMin?: number | null
  powerThresholdMax?: number | null
  minimumTransactionAmount?: number | null
  isFreeSessionExempt?: boolean
  status: 'active' | 'inactive' | 'draft'
  lastUpdated: string
}

const mapBackendToFrontend = (backend: any): TaxRule => {
  // Backend is now converted to camelCase by CaseConverter
  const locationType: 'states' | 'zip' = backend.states && backend.states.length > 0 ? 'states' : 'zip'
  const locationDetails = locationType === 'states' ? backend.states : backend.zipCodes
  
  // Determine rate type: if tax_rate_percent is 0, check tax_rate_kwh
  const taxRatePercent = parseFloat(backend.taxRatePercent || '0')
  const taxRateKwh = parseFloat(backend.taxRateKwh || '0')
  
  let rateType: 'perKwh' | 'percentage'
  let rateCents: number | undefined
  let ratePercentage: number | undefined
  
  if (taxRatePercent === 0) {
    // If tax_rate_percent is 0, check tax_rate_kwh
    if (taxRateKwh !== 0) {
      rateType = 'perKwh'
      rateCents = taxRateKwh
      ratePercentage = undefined
    } else {
      // Both are 0, default to perKwh
      rateType = 'perKwh'
      rateCents = undefined
      ratePercentage = undefined
    }
  } else {
    // tax_rate_percent is not 0, use percentage
    rateType = 'percentage'
    rateCents = undefined
    ratePercentage = taxRatePercent
  }
  
  // For percentage type, we need to determine appliesTo - this might need to come from backend
  // For now, defaulting to energyCostOnly
  const appliesTo: 'allCost' | 'energyCostOnly' = 'energyCostOnly'
  
  const powerThresholdMin = backend.powerThresholdMin ? parseFloat(backend.powerThresholdMin) : null
  const powerThresholdMax = backend.powerThresholdMax ? parseFloat(backend.powerThresholdMax) : null
  const minimumTransactionAmount = backend.minimumTransactionAmount ? parseFloat(backend.minimumTransactionAmount) : null
  const isFreeSessionExempt = backend.isFreeSessionExempt ?? false

  return {
    id: backend.id.toString(),
    name: backend.taxName,
    description: backend.taxDescription,
    locationType,
    locationDetails,
    rateType,
    rateCents,
    ratePercentage,
    appliesTo,
    powerThresholdMin,
    powerThresholdMax,
    minimumTransactionAmount,
    isFreeSessionExempt,
    status: backend.status,
    lastUpdated: backend.lastModifiedAt,
  }
}

export const useTaxConfigurations = () => {
  const [taxRules, setTaxRules] = useState<TaxRule[]>([])
  const [pagination, setPagination] = useState<PaginationMeta>({
    currentPage: 1,
    perPage: 8,
    totalEntries: 0,
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<SwtchError>()
  const [sortByLastUpdated, setSortByLastUpdated] = useState<'desc' | 'asc' | 'none'>('desc')
  const [filters, setFilters] = useState<TaxConfigurationFilters>({})

  const fetchTaxConfigurations = (
    page: number = 1,
    perPage: number = 8,
    sortOrder?: 'desc' | 'asc' | 'none',
    filterParams?: TaxConfigurationFilters,
  ) => {
    setLoading(true)
    setError(undefined)
    const sort = sortOrder !== undefined ? sortOrder : sortByLastUpdated
    const activeFilters = filterParams !== undefined ? filterParams : filters
    GetTaxConfigurations(page, perPage, sort, activeFilters)
      .then((resp) => {
        setTaxRules(resp.data.map(mapBackendToFrontend))
        setPagination(resp.pagination)
      })
      .catch((err) => setError(err))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchTaxConfigurations(1, 8, 'desc', {})
  }, [])

  const handlePaginationChange = (page: number, pageSize?: number) => {
    const perPage = pageSize || pagination.perPage
    fetchTaxConfigurations(page, perPage, sortByLastUpdated, filters)
  }

  const handleSortChange = (sortOrder: 'desc' | 'asc' | 'none') => {
    setSortByLastUpdated(sortOrder)
    // Reset to first page when sort changes
    fetchTaxConfigurations(1, pagination.perPage, sortOrder, filters)
  }

  const handleFilterChange = (newFilters: TaxConfigurationFilters) => {
    setFilters(newFilters)
    // Reset to first page when filters change
    fetchTaxConfigurations(1, pagination.perPage, sortByLastUpdated, newFilters)
  }

  const handleClearFilters = () => {
    const clearedFilters: TaxConfigurationFilters = {}
    setFilters(clearedFilters)
    fetchTaxConfigurations(1, pagination.perPage, sortByLastUpdated, clearedFilters)
  }

  return {
    taxRules,
    pagination,
    loading,
    error,
    sortByLastUpdated,
    filters,
    fetchTaxConfigurations,
    handlePaginationChange,
    handleSortChange,
    handleFilterChange,
    handleClearFilters,
  }
}

